'use client'

import React, { useState } from 'react'
import Image from 'next/image'
import Link from 'next/link'
import { ArrowRight, ShoppingBag, Tag, CheckCircle, Flame, ChevronRight } from 'lucide-react'
import { BrandedProduct, BrandedProductMerchant } from '@/services/dataTypes'
import { discardHTMLTags, getBaseImageUrl, getCurrencySymbol, getFinalDiscountTag, getMerchantHref, getProductDetailHref } from '@/constants/hooks'
import OfferOutUrl from '@/components/shared/OfferOutUrl'
import OfferDetailsToggle from './OfferDetailsToggle'

// ─── Types ────────────────────────────────────────────────────────────────────

export interface BrandGroup {
    merchant: BrandedProductMerchant
    products: BrandedProduct[]
}

interface Props {
    brands: BrandGroup[]
    domain: string
    slugType: string
    merSlug?: string
    sectionHeading?: string
    sectionSubheading?: string
}

// ─── Product Card ─────────────────────────────────────────────────────────────

function ProductCard({ product, domain, slugType, merSlug = 'store' }: { product: BrandedProduct; domain: string; slugType: string; merSlug?: string }) {
    const type = product?.offer_type?.name
    const originalPrice = product?.original_price ? parseFloat(product.original_price) : 0
    const salePrice = product?.sale_price ? parseFloat(product.sale_price) : 0
    const discountPct = originalPrice > 0 && salePrice > 0
        ? Math.round(((originalPrice - salePrice) / originalPrice) * 100) : null
    const finalDiscountTag = getFinalDiscountTag(product?.offer_title, discountPct)
    const isCoupon = !!product?.coupon_code
    const currency = getCurrencySymbol(product?.currency)
    const imageSrc = getBaseImageUrl(domain, product?.product_image, '')
    const productDetailUrl = product?.slug && product?.is_detail === 1
        ? getProductDetailHref(product.merchant, slugType, product.slug, product?.category?.slug)
        : null
    const merchantHref = product?.merchant ? getMerchantHref(product.merchant, merSlug, slugType) : '/products'

    return (
        <div className="group relative bg-white rounded-3xl border border-slate-200/80 hover:border-[#5C7A5C]/40 shadow-[0_4px_25px_rgba(0,0,0,0.03)] hover:shadow-[0_20px_40px_rgba(45,62,45,0.12)] transition-all duration-300 flex flex-col h-full overflow-hidden hover:-translate-y-1">
            {/* Badge */}
            {finalDiscountTag && (
                <div className="absolute top-2.5 right-2.5 z-10 inline-flex items-center px-2 py-0.5 rounded-md text-[9px] sm:text-[10px] font-black bg-[#E8F0E8] text-[#2D3E2D] border border-[#5C7A5C]/20 shadow-2xs truncate">
                    {finalDiscountTag}
                </div>
            )}

            {/* Image Area matching Theme 5 (h-36 sm:h-40) */}
            <div className="relative w-full h-36 sm:h-40 p-4 flex items-center justify-center overflow-hidden bg-[#FAF7F2]">
                <Image
                    src={imageSrc}
                    alt={discardHTMLTags(product?.offer_title) || 'Product'}
                    className="object-contain max-h-[110px] w-auto h-auto group-hover:scale-105 transition-transform duration-500"
                    height={140}
                    width={140}
                    unoptimized
                />
            </div>

            {/* Card Content */}
            <div className="p-5 pt-3 flex flex-col flex-grow justify-between">
                <div>
                    {productDetailUrl ? (
                        <Link href={productDetailUrl}
                            className="text-xs sm:text-sm font-bold text-[#2D3E2D] leading-snug line-clamp-2 hover:text-[#5C7A5C] transition-colors mb-2"
                            style={{ fontFamily: 'Georgia, serif' }}>
                            {discardHTMLTags(product?.offer_title)}
                        </Link>
                    ) : (
                        <h4 className="text-xs sm:text-sm font-bold text-[#2D3E2D] leading-snug line-clamp-2 mb-2"
                            style={{ fontFamily: 'Georgia, serif' }}>
                            {discardHTMLTags(product?.offer_title)}
                        </h4>
                    )}

                    {type === 'product' && (salePrice > 0 || originalPrice > 0) ? (
                        <div className="flex items-baseline gap-1.5 flex-wrap my-1">
                            {salePrice > 0 && <span className="text-sm font-black text-[#2D3E2D]">{currency}{salePrice.toFixed(2)}</span>}
                            {originalPrice > 0 && salePrice > 0 && <span className="text-[10px] line-through text-slate-400">{currency}{originalPrice.toFixed(2)}</span>}
                            {discountPct && <span className="text-[9px] font-black px-1.5 py-0.5 rounded-full bg-[#E8F0E8] text-[#3D5A3D]">-{discountPct}%</span>}
                        </div>
                    ) : (
                        <div className="flex items-center gap-1 text-[10px] font-semibold text-[#5C7A5C] my-1">
                            <CheckCircle size={10} /> Verified deal
                        </div>
                    )}
                </div>

                <div className="mt-3 pt-2 border-t border-slate-100 flex flex-col gap-2">
                    <div className="flex justify-end">
                        <OfferDetailsToggle
                            offer={product}
                            merchantHref={merchantHref}
                            imageSrc={product?.product_image ?? ''}
                            merchantImg={product?.merchant?.merchant_logo ?? ''}
                            domain={domain}
                            type="anchor"
                            modalType="simple"
                        />
                    </div>

                    <OfferOutUrl
                        unique_id={product?.unique_id}
                        outUrl={product?.url}
                        merchantHref={merchantHref}
                        domain={domain}
                        customClass="w-full"
                    >
                        <div
                            className="w-full flex items-center justify-center gap-1.5 py-2.5 px-3 rounded-xl text-xs font-bold text-[#2D3E2D] transition-all hover:shadow-sm"
                            style={{ background: 'linear-gradient(135deg, #F5C542 0%, #E5B224 100%)' }}
                        >
                            <span>{isCoupon ? 'Get Code' : (type === 'product' ? 'Buy Now' : 'Get Deal')}</span>
                            <ArrowRight size={12} />
                        </div>
                    </OfferOutUrl>
                </div>
            </div>
        </div>
    )
}

// ─── Main Client Component ────────//

const BrandProductsClient = ({ brands, domain, slugType, merSlug = 'store', sectionHeading = 'Brand', sectionSubheading = 'Products' }: Props) => {
    const [activeId, setActiveId] = useState<number>(brands[0]?.merchant?.id)
    const activeBrand = brands.find(b => b.merchant.id === activeId) ?? brands[0]

    if (!brands.length) return null

    const activeBrandHref = activeBrand?.merchant ? getMerchantHref(activeBrand.merchant, merSlug, slugType) : '/products';

    return (
        <section className="py-20 px-6 relative overflow-hidden"
            aria-labelledby="brand-products-heading">
            <div className="absolute inset-0 pointer-events-none opacity-[0.03]"
                style={{ backgroundImage: 'radial-gradient(circle, #2D3E2D 1px, transparent 1px)', backgroundSize: '28px 28px' }} />

            <div className="max-w-6xl mx-auto relative">

                {/* Header */}
                <div className="flex items-end justify-between mb-8">
                    <div>
                        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-widest mb-3"
                            style={{ background: 'rgba(45,62,45,0.08)', border: '1px solid rgba(45,62,45,0.15)', color: '#5C7A5C' }}>
                            <ShoppingBag size={11} /> Top Brands
                        </div>
                        <h2 id="brand-products-heading" className="text-4xl font-black"
                            style={{ fontFamily: 'Georgia, serif', color: '#2D3E2D' }}>
                            {sectionHeading}{' '}
                            <span style={{ color: '#F5C542' }}>{sectionSubheading}</span>
                        </h2>
                    </div>
                    <Link href="/products"
                        className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-full text-sm font-bold transition-all hover:opacity-80 flex-shrink-0 self-start sm:self-auto w-fit whitespace-nowrap"
                        style={{ background: '#2D3E2D', color: '#F5C542' }}>
                        All Products <ArrowRight size={13} />
                    </Link>
                </div>

                {/* Brand Selector Tabs */}
                {brands.length > 1 && (
                    <div className="flex items-center gap-2.5 overflow-x-auto pb-4 mb-6" style={{ scrollbarWidth: 'none' }}>
                        {brands.map((b) => {
                            const isActive = b.merchant.id === (activeBrand?.merchant?.id ?? activeId)
                            return (
                                <button
                                    key={b.merchant.id}
                                    onClick={() => setActiveId(b.merchant.id)}
                                    className="flex items-center gap-2 px-4 py-2.5 rounded-2xl text-xs font-bold transition-all flex-shrink-0 cursor-pointer"
                                    style={{
                                        background: isActive ? '#2D3E2D' : '#FFFFFF',
                                        color: isActive ? '#F5C542' : '#2D3E2D',
                                        border: `1px solid ${isActive ? '#2D3E2D' : 'rgba(92,122,92,0.18)'}`,
                                        boxShadow: isActive ? '0 4px 14px rgba(45,62,45,0.15)' : '0 2px 6px rgba(45,62,45,0.03)',
                                    }}>
                                    <div className="w-5 h-5 rounded-lg flex items-center justify-center p-0.5 overflow-hidden"
                                        style={{ background: isActive ? 'rgba(250,247,242,0.15)' : '#E8F0E8' }}>
                                        <Image
                                            src={getBaseImageUrl(domain, b.merchant.merchant_logo, '')}
                                            alt={b.merchant.merchant_name}
                                            width={16}
                                            height={16}
                                            className="object-contain"
                                        />
                                    </div>
                                    <span>{b.merchant.merchant_name}</span>
                                    <span className="px-1.5 py-0.5 rounded-full text-[10px]"
                                        style={{ background: isActive ? 'rgba(245,197,66,0.2)' : 'rgba(92,122,92,0.1)', color: isActive ? '#F5C542' : '#5C7A5C' }}>
                                        {b.products.length}
                                    </span>
                                </button>
                            )
                        })}
                    </div>
                )}

                {/* Active brand panel */}
                {activeBrand && (
                    <div>
                        {/* Brand header bar */}
                        <div className="flex items-center justify-between py-3 mb-6 border-b border-slate-200/80">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-xl flex items-center justify-center p-1.5 flex-shrink-0 bg-white border border-slate-200/60 shadow-2xs">
                                    <Image src={getBaseImageUrl(domain, activeBrand.merchant.merchant_logo, '')}
                                        alt={activeBrand.merchant.merchant_name} width={28} height={28} className="object-contain" />
                                </div>
                                <div>
                                    <p className="text-[10px] font-semibold uppercase tracking-widest" style={{ color: 'rgba(45,62,45,0.4)' }}>Now Browsing</p>
                                    <h3 className="text-base font-black" style={{ color: '#2D3E2D', fontFamily: 'Georgia, serif' }}>
                                        {activeBrand.merchant.merchant_name}
                                    </h3>
                                </div>
                            </div>
                        </div>

                        {/* Products grid */}
                        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-6">
                            {activeBrand.products.slice(0, 8).map(product => (
                                <ProductCard key={product.unique_id} product={product} domain={domain} slugType={slugType} merSlug={merSlug} />
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </section>
    )
}

export default BrandProductsClient
